import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, BatteryCharging, Check, ChevronRight, PlugZap, RefreshCw, Truck, Wallet, Wrench, Zap } from "lucide-react";
import { CtaBand, EASE, IMG, Line, Reveal } from "@/components/shared";
import { useLang } from "@/i18n";

export default function Platform() {
  const { t } = useLang();
  useEffect(() => { document.title = "The Platform | AsaanCharge"; }, []);
  const model = [
    { icon: Wallet, title: t("model1T"), copy: t("model1P") },
    { icon: RefreshCw, title: t("model2T"), copy: t("model2P") },
    { icon: Wrench, title: t("model3T"), copy: t("model3P") },
  ];
  const flow = [
    { icon: Zap, number: "01", title: t("flow1T"), copy: t("flow1P") },
    { icon: Truck, number: "02", title: t("flow2T"), copy: t("flow2P") },
    { icon: PlugZap, number: "03", title: t("flow3T"), copy: t("flow3P") },
    { icon: BatteryCharging, number: "04", title: t("flow4T"), copy: t("flow4P") },
  ];
  const specs = [
    [t("specCap"), t("specCapV"), "capacity"],
    [t("specOut"), t("specOutV"), "output"],
    [t("specScale"), t("specScaleV"), "scalability"],
    [t("specBy"), t("specByV"), "charged-by"],
    [t("specMob"), t("specMobV"), "mobility"],
  ];
  const stationPoints = [t("stPoint1"), t("stPoint2"), t("stPoint3")];
  const apps = [
    { img: IMG.ev, tag: t("tagFlagship"), flag: true, title: t("app1T"), copy: t("app1P") },
    { img: IMG.events, tag: t("tagEvents"), title: t("app2T"), copy: t("app2P") },
    { img: IMG.delivery, tag: t("tagBackup"), title: t("app3T"), copy: t("app3P") },
  ];
  const benefits = [
    { title: t("ben1T"), copy: t("ben1P") },
    { title: t("ben2T"), copy: t("ben2P") },
    { title: t("ben3T"), copy: t("ben3P") },
    { title: t("ben4T"), copy: t("ben4P") },
  ];
  return (
    <>
      <section className="page-hero container">
        <motion.div className="eyebrow" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: EASE }}><span className="pulse-dot" /> {t("platEyebrow")}</motion.div>
        <h1><Line delay={0.22}>{t("platL1")}</Line><Line delay={0.34}><em>{t("platL2")}</em></Line></h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.6, ease: EASE }}>{t("platSub")}</motion.p>
      </section>

      <section className="section model-section">
        <div className="container">
          <Reveal className="section-heading"><div><div className="eyebrow">{t("modelEyebrow")}</div><h2>{t("modelL1")}<br /><em>{t("modelL2")}</em></h2></div><p>{t("modelP")}</p></Reveal>
          <div className="model-grid">{model.map(({ icon: Icon, title, copy }, index) => <Reveal className="model-card" key={title} delay={index * 0.1} data-testid={`model-card-${index + 1}`}><div className="teaser-icon"><Icon /></div><h3>{title}</h3><p>{copy}</p></Reveal>)}</div>
        </div>
      </section>

      <section className="section process-section" id="how-it-works">
        <div className="container">
          <Reveal><div className="eyebrow">{t("flowEyebrow")}</div><div className="process-title-row"><h2>{t("flowL1")}<br /><em>{t("flowL2")}</em></h2><p>{t("flowP")}</p></div></Reveal>
          <div className="flow-grid">{flow.map(({ icon: Icon, number, title, copy }, index) => <Reveal className="flow-card" key={title} delay={index * 0.1} data-testid={`flow-step-${number}`}><div className="flow-icon"><Icon /></div><span className="flow-number">{number}</span><h3>{title}</h3><p>{copy}</p>{index < 3 && <ChevronRight className="flow-arrow" />}</Reveal>)}</div>
        </div>
      </section>

      <section className="section module-section container">
        <Reveal className="module-media">
          <div className="media-frame module-frame">
            <img src={IMG.module} alt="AsaanCharger1 portable battery module" />
            <div className="media-shade" />
            <div className="media-tag"><span className="pulse-dot" /> {t("modTag")}</div>
          </div>
          <div className="frame-caption">{t("modCaption")}</div>
        </Reveal>
        <Reveal className="module-copy" delay={0.12}>
          <div className="eyebrow">{t("modEyebrow")}</div>
          <h2>{t("modL1")}<em>{t("modL2")}</em></h2>
          <p>{t("modP")}</p>
          <div className="spec-list">{specs.map(([label, value, id]) => <div className="spec-row" key={id} data-testid={`spec-${id}`}><span>{label}</span><b>{value}</b></div>)}</div>
          <small className="spec-note">{t("specNote")}</small>
        </Reveal>
      </section>

      <section className="section station-section container" data-testid="station-section">
        <Reveal className="module-copy">
          <div className="eyebrow">{t("stEyebrow")}</div>
          <h2>{t("stL1")}<br /><em>{t("stL2")}</em></h2>
          <p>{t("stP")}</p>
          <div className="station-points">{stationPoints.map((point, index) => <span key={point} data-testid={`station-point-${index + 1}`}><Check size={15} /> {point}</span>)}</div>
        </Reveal>
        <Reveal className="module-media" delay={0.12}>
          <div className="media-frame station-frame">
            <img src={IMG.station} alt="Battery racks at the AsaanCharge hub" />
            <div className="media-shade" />
          </div>
          <div className="frame-caption">{t("stCaption")}</div>
        </Reveal>
      </section>

      <section className="section apps-section">
        <div className="container">
          <Reveal className="section-heading"><div><div className="eyebrow">{t("appsEyebrow")}</div><h2>{t("appsL1")}<br /><em>{t("appsL2")}</em></h2></div><p>{t("appsP")}</p></Reveal>
          <div className="apps-grid">{apps.map((app, index) => <Reveal className={`app-card ${app.flag ? "app-flag" : ""}`} key={app.title} delay={index * 0.1} data-testid={`app-card-${index + 1}`}><div className="app-img"><img src={app.img} alt={app.title} /><span className="app-tag">{app.tag}</span></div><div className="app-body"><h3>{app.title}</h3><p>{app.copy}</p></div></Reveal>)}</div>
        </div>
      </section>

      <section className="section difference-section container" id="different">
        <Reveal className="difference-intro"><div className="eyebrow">{t("diffEyebrow")}</div><h2>{t("diffL1")}<br /><em>{t("diffL2")}</em></h2><p>{t("diffP")}</p></Reveal>
        <div className="benefit-list">{benefits.map((benefit, index) => <Reveal className="benefit-row" key={benefit.title} delay={index * 0.08} y={26} data-testid={`benefit-${index + 1}`}><span>0{index + 1}</span><div><h3>{benefit.title}</h3><p>{benefit.copy}</p></div><ArrowRight /></Reveal>)}</div>
      </section>

      <CtaBand />
    </>
  );
}
